import { useState, useEffect, useCallback } from "react";
import { GitCommit, Loader2, AlertCircle, CheckCircle2, ArrowLeftRight } from "lucide-react";
import { useProject } from "../contexts/ProjectContext";
import { authFetch } from "../utils/authFetch";
import BranchSelector from "../components/docs/BranchSelector";
import {
  useTreeDiff,
} from "../components/docs/TreeDiffViewer";
import TwoColumnDiffTree from "../components/docs/TwoColumnDiffTree";

interface BranchInfo { id: string; name: string; headCommitId: string | null; }
interface CommitEntry { id: string; sequence: number; message: string | null; createdAt: string | null; }

function CommitChain({
  commits, selectedId, onSelect, loading, side,
}: {
  commits: CommitEntry[]; selectedId: string; onSelect: (id: string) => void;
  loading: boolean; side: "source" | "target";
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-6 text-slate-400 text-[11px]">
        <Loader2 className="size-3.5 animate-spin" /> Loading commits…
      </div>
    );
  }
  if (commits.length === 0) {
    return <div className="py-5 text-center text-[11px] text-slate-400 italic">No commits yet</div>;
  }

  const accent = side === "source"
    ? { ring: "ring-indigo-200", bg: "bg-indigo-50", dot: "bg-indigo-500 border-indigo-500", hoverDot: "group-hover:border-indigo-400", text: "text-indigo-700", badge: "bg-indigo-100 text-indigo-600" }
    : { ring: "ring-violet-200", bg: "bg-violet-50", dot: "bg-violet-500 border-violet-500", hoverDot: "group-hover:border-violet-400", text: "text-violet-700", badge: "bg-violet-100 text-violet-600" };

  return (
    <div className="space-y-0">
      {commits.map((c, i) => {
        const active = selectedId === c.id;
        const isLast = i === commits.length - 1;
        const isFirst = i === 0;
        return (
          <div key={c.id} className="flex gap-0 group relative">
            <div className="flex flex-col items-center shrink-0 w-5 relative">
              {!isFirst && <div className="w-px flex-1 bg-slate-200" />}
              {isFirst && <div className="flex-1" />}
              <div className={`size-2 rounded-full border-[1.5px] shrink-0 transition-all ${active ? `${accent.dot} shadow-sm` : `bg-white border-slate-300 ${accent.hoverDot}`}`} />
              {!isLast && <div className="w-px flex-1 bg-slate-200" />}
              {isLast && <div className="flex-1" />}
            </div>
            <button onClick={() => onSelect(active ? "" : c.id)}
              className={`flex-1 min-w-0 text-left px-2.5 py-1.5 rounded-md transition-all my-0.5 ${active ? `${accent.bg} ring-1 ${accent.ring}` : "hover:bg-slate-50/80"}`}>
              <div className="flex items-center gap-1.5">
                <span className={`text-[9px] font-mono px-1 py-px rounded shrink-0 ${active ? accent.badge : "bg-slate-100 text-slate-500"}`}>#{c.sequence}</span>
                {isFirst && <span className="text-[8px] font-semibold px-1 py-px rounded bg-emerald-100 text-emerald-700 shrink-0">HEAD</span>}
                <span className={`text-[11px] truncate leading-tight ${active ? `${accent.text} font-medium` : "text-slate-600"}`}>{c.message || "No message"}</span>
              </div>
              {c.createdAt && (
                <div className="text-[9px] text-slate-400 mt-0.5 pl-0.5">
                  {new Date(c.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default function BranchDiffPage() {
  const { currentProject } = useProject();
  const projectSlug = currentProject?.slug;

  const [branches, setBranches] = useState<BranchInfo[]>([]);
  const [sourceId, setSourceId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [sourceCommitId, setSourceCommitId] = useState("");
  const [targetCommitId, setTargetCommitId] = useState("");
  const [sourceCommits, setSourceCommits] = useState<CommitEntry[]>([]);
  const [targetCommits, setTargetCommits] = useState<CommitEntry[]>([]);
  const [sourceCommitsLoading, setSourceCommitsLoading] = useState(false);
  const [targetCommitsLoading, setTargetCommitsLoading] = useState(false);
  const [compared, setCompared] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sourceBranch = branches.find(b => b.id === sourceId);
  const targetBranch = branches.find(b => b.id === targetId);
  const effectiveSourceCommit = sourceCommitId || sourceBranch?.headCommitId || null;
  const effectiveTargetCommit = targetCommitId || targetBranch?.headCommitId || null;
  const diffBranchId = sourceId || targetId;

  const treeDiff = useTreeDiff({
    projectSlug: projectSlug || "",
    branchId: diffBranchId,
    commitA: compared ? effectiveTargetCommit : null,
    commitB: compared ? effectiveSourceCommit : null,
  });

  // Fetch branches
  useEffect(() => {
    if (!projectSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`/api/projects/${projectSlug}/studio/branches`);
        if (cancelled || !res.ok) return;
        const data = await res.json();
        setBranches(data.map((b: any) => ({ id: b.id, name: b.name, headCommitId: b.headCommitId || null })));
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [projectSlug]);

  // Fetch source commits
  useEffect(() => {
    if (!projectSlug || !sourceId) { setSourceCommits([]); return; }
    let cancelled = false;
    setSourceCommitsLoading(true);
    (async () => {
      try {
        const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${sourceId}/commits?limit=30`);
        if (cancelled || !res.ok) return;
        setSourceCommits((await res.json()).commits || []);
      } catch { /* ignore */ }
      finally { if (!cancelled) setSourceCommitsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [projectSlug, sourceId]);

  // Fetch target commits
  useEffect(() => {
    if (!projectSlug || !targetId) { setTargetCommits([]); return; }
    let cancelled = false;
    setTargetCommitsLoading(true);
    (async () => {
      try {
        const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${targetId}/commits?limit=30`);
        if (cancelled || !res.ok) return;
        setTargetCommits((await res.json()).commits || []);
      } catch { /* ignore */ }
      finally { if (!cancelled) setTargetCommitsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [projectSlug, targetId]);

  // Auto-select head commit
  useEffect(() => { if (sourceCommits.length > 0 && !sourceCommitId) setSourceCommitId(sourceCommits[0].id); }, [sourceCommits]);
  useEffect(() => { if (targetCommits.length > 0 && !targetCommitId) setTargetCommitId(targetCommits[0].id); }, [targetCommits]);

  // Reset on branch change
  useEffect(() => { setSourceCommitId(""); setCompared(false); }, [sourceId]);
  useEffect(() => { setTargetCommitId(""); setCompared(false); }, [targetId]);

  const handleCompare = useCallback(() => {
    if (!effectiveSourceCommit || !effectiveTargetCommit) { setError("Both branches need at least one commit to compare."); return; }
    setError(null);
    setCompared(true);
  }, [effectiveSourceCommit, effectiveTargetCommit]);

  useEffect(() => {
    if (compared && effectiveSourceCommit && effectiveTargetCommit) treeDiff.fetchTreeDiff();
  }, [compared, effectiveSourceCommit, effectiveTargetCommit]);

  const handleSwap = useCallback(() => {
    const t = targetId; const tc = targetCommitId;
    setTargetId(sourceId); setTargetCommitId(sourceCommitId);
    setSourceId(t); setSourceCommitId(tc);
    setCompared(false);
  }, [targetId, targetCommitId, sourceId, sourceCommitId]);

  const canCompare = sourceId && targetId && effectiveSourceCommit && effectiveTargetCommit && effectiveSourceCommit !== effectiveTargetCommit;
  const hasResults = compared && !treeDiff.loading && treeDiff.mergedTree.length > 0;
  const isEmpty = compared && !treeDiff.loading && treeDiff.mergedTree.length === 0;

  return (
    <div className="space-y-3 px-6">
      {/* Branch selectors — aligned to commit panel grid */}
      <div className="grid grid-cols-[1fr_auto_1fr] gap-2">
        <BranchSelector branches={branches} selectedId={targetId} onChange={setTargetId} label="Target (base)" placeholder="Select branch…" />
        <div className="w-[34px]" />
        <BranchSelector branches={branches} selectedId={sourceId} onChange={setSourceId} label="Source (compare)" placeholder="Select branch…" />
      </div>

      {/* Commit chains with swap button between them */}
      {(targetId || sourceId) && (
        <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-stretch">
          <div className="border border-slate-200 rounded-lg overflow-hidden flex flex-col h-[200px]">
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 border-b border-slate-200 shrink-0">
              <GitCommit className="size-3 text-slate-400" />
              <span className="text-[9px] font-medium text-slate-500 uppercase tracking-wider">Commits</span>
            </div>
            <div className="flex-1 overflow-y-auto p-2 scrollbar-mac">
              {targetId
                ? <CommitChain commits={targetCommits} selectedId={targetCommitId} onSelect={setTargetCommitId} loading={targetCommitsLoading} side="target" />
                : <div className="flex items-center justify-center h-full text-[11px] text-slate-400 italic">Select a target branch</div>}
            </div>
          </div>

          <div className="w-[34px] flex items-center justify-center">
            <button onClick={handleSwap} className="p-1.5 rounded-md border border-slate-200 text-slate-400 hover:text-indigo-500 hover:border-indigo-200 hover:bg-indigo-50/50 transition-colors" title="Swap branches">
              <ArrowLeftRight className="size-3.5" />
            </button>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden flex flex-col h-[200px]">
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 border-b border-slate-200 shrink-0">
              <GitCommit className="size-3 text-slate-400" />
              <span className="text-[9px] font-medium text-slate-500 uppercase tracking-wider">Commits</span>
            </div>
            <div className="flex-1 overflow-y-auto p-2 scrollbar-mac">
              {sourceId
                ? <CommitChain commits={sourceCommits} selectedId={sourceCommitId} onSelect={setSourceCommitId} loading={sourceCommitsLoading} side="source" />
                : <div className="flex items-center justify-center h-full text-[11px] text-slate-400 italic">Select a source branch</div>}
            </div>
          </div>
        </div>
      )}

      {/* Compare */}
      {canCompare && (
        <div className="flex justify-end">
          <button onClick={handleCompare} disabled={treeDiff.loading} className="px-4 py-1.5 rounded-md text-[11px] font-medium bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-40 transition-colors">
            {treeDiff.loading ? "Comparing…" : "Compare"}
          </button>
        </div>
      )}

      {treeDiff.loading && (
        <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-2">
          <Loader2 className="size-5 animate-spin" />
          <span className="text-[12px]">Comparing…</span>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-[12px] text-red-600">
          <AlertCircle className="size-4 shrink-0" /><span>{error}</span>
        </div>
      )}
      {isEmpty && !error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-[12px] text-emerald-600">
          <CheckCircle2 className="size-4 shrink-0" /><span>Branches are identical — no differences found.</span>
        </div>
      )}
      {hasResults && (
        <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
          <div className="flex-1 min-h-[200px] overflow-hidden flex flex-col" style={{ height: "calc(100vh - 340px)" }}>
            <TwoColumnDiffTree
              beforeNodes={treeDiff.beforeNodes}
              afterNodes={treeDiff.afterNodes}
              beforeRootOrder={treeDiff.beforeRootOrder}
              afterRootOrder={treeDiff.afterRootOrder}
              changes={treeDiff.rawChanges}
              moves={treeDiff.moves}
              beforeLabel="Target"
              afterLabel="Source"
              onSelectChange={treeDiff.handleSelectChange}
              selectedDetail={treeDiff.selectedDetail}
              detailLoading={treeDiff.detailLoading}
              onClearDetail={treeDiff.clearDetail}
            />
          </div>
        </div>
      )}
    </div>
  );
}
